/**
 * Client-side drawdown summary metrics derived from risk-metrics `drawdown_data`.
 */

export type DrawdownSeriesPoint = {
  date: string;
  drawdown: number;
  portfolio_value: number;
};

export type DrawdownSeriesMetrics = {
  /** Total sampled days spent below the running equity high (all under-high days combined). */
  daysInDrawdown: number | null;
  /** Longest consecutive stretch below the running equity high (calendar days, inclusive). */
  longestDrawdownDays: number | null;
  currentDrawdown: number | null;
  maxDrawdown: number | null;
};

const IN_DRAWDOWN_EPS = 1e-9;

export function drawdownSeriesYmd(date: string): string {
  return date.length >= 10 ? date.slice(0, 10) : date;
}

/** Whole calendar days between two YYYY-MM-DD strings (0 when same day). */
export function calendarDayDiff(startYmd: string, endYmd: string): number {
  const start = Date.parse(`${startYmd}T12:00:00.000Z`);
  const end = Date.parse(`${endYmd}T12:00:00.000Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return 0;
  return Math.max(0, Math.round((end - start) / 86_400_000));
}

function absDrawdown(value: number): number {
  return Math.abs(Number(value) || 0);
}

function isInDrawdown(drawdown: number): boolean {
  return absDrawdown(drawdown) > IN_DRAWDOWN_EPS;
}

/** Inclusive calendar length of a streak spanning firstYmd..lastYmd. */
function inclusiveCalendarDays(firstYmd: string, lastYmd: string): number {
  return calendarDayDiff(firstYmd, lastYmd) + 1;
}

export function formatDayCount(days: number | null | undefined): string {
  if (days === null || days === undefined || !Number.isFinite(days)) {
    return "N/A";
  }
  const n = Math.max(0, Math.round(days));
  return n === 1 ? "1 day" : `${n} days`;
}

/**
 * Derive simple drawdown duration metrics from drawdown series (sorted by date ascending).
 */
export function computeDrawdownSeriesMetrics(
  series: DrawdownSeriesPoint[] | null | undefined
): DrawdownSeriesMetrics {
  if (!series?.length) {
    return {
      daysInDrawdown: null,
      longestDrawdownDays: null,
      currentDrawdown: null,
      maxDrawdown: null,
    };
  }

  const points = [...series].sort((a, b) =>
    drawdownSeriesYmd(a.date).localeCompare(drawdownSeriesYmd(b.date))
  );

  let daysInDrawdown = 0;
  let longestDrawdownDays = 0;
  let maxDrawdown = 0;

  let i = 0;
  while (i < points.length) {
    if (!isInDrawdown(points[i].drawdown)) {
      i += 1;
      continue;
    }

    const streakStart = i;
    while (i < points.length && isInDrawdown(points[i].drawdown)) {
      const dd = absDrawdown(points[i].drawdown);
      daysInDrawdown += 1;
      if (dd > maxDrawdown) {
        maxDrawdown = dd;
      }
      i += 1;
    }

    const firstYmd = drawdownSeriesYmd(points[streakStart].date);
    const lastYmd = drawdownSeriesYmd(points[i - 1].date);
    longestDrawdownDays = Math.max(longestDrawdownDays, inclusiveCalendarDays(firstYmd, lastYmd));
  }

  const last = points[points.length - 1];
  return {
    daysInDrawdown,
    longestDrawdownDays,
    currentDrawdown: absDrawdown(last.drawdown),
    maxDrawdown: maxDrawdown > IN_DRAWDOWN_EPS ? maxDrawdown : 0,
  };
}

export type EquityPeakChartPoint = {
  date: string;
  label: string;
  equity: number;
  peak: number;
};

/** Running peak of portfolio_value for equity/peak path charts. */
export function buildEquityPeakSeries(
  series: DrawdownSeriesPoint[] | null | undefined,
  labelForYmd: (ymd: string) => string
): EquityPeakChartPoint[] {
  if (!series?.length) return [];

  const points = [...series].sort((a, b) =>
    drawdownSeriesYmd(a.date).localeCompare(drawdownSeriesYmd(b.date))
  );

  let peak = Number.NEGATIVE_INFINITY;
  return points.map((point) => {
    const ymd = drawdownSeriesYmd(point.date);
    const equity = Number(point.portfolio_value) || 0;
    peak = Math.max(peak, equity);
    return {
      date: point.date,
      label: labelForYmd(ymd),
      equity,
      peak: Number.isFinite(peak) ? peak : equity,
    };
  });
}
